import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { presenceLabel, isOnline } from "@/lib/presence";
import { Loader2, Send, ArrowLeft } from "lucide-react";
import Avatar from "@/components/Avatar";

function timeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function ChatThread() {
  const { peerId } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [peer, setPeer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);
  const lastTypingPing = useRef(0);

  const load = async () => {
    try {
      const res = await api.get(`/messages/${peerId}`);
      setPeer(res.data.peer);
      setMessages(res.data.messages || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [peerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async (e) => {
    e?.preventDefault();
    const v = text.trim();
    if (!v || sending) return;
    setSending(true);
    // Optimistic
    const optimistic = {
      id: `tmp-${Date.now()}`,
      from_id: user.id,
      to_id: peerId,
      text: v,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages((m) => [...m, optimistic]);
    setText("");
    try {
      await api.post(`/messages/${peerId}`, { text: v });
      load();
    } catch (err) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setError(err?.response?.data?.detail || "Send failed");
      setText(v);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>;
  }

  if (error && !peer) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 text-center" data-testid="chat-error">
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{error}</p>
        <button onClick={() => nav("/messages")} className="mt-4 px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm">
          {t("messages")}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto h-[calc(100vh-72px)] md:h-[calc(100vh-72px)] flex flex-col" data-testid="chat-thread">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-[hsl(var(--border))] bg-white">
        <button onClick={() => nav("/messages")} className="p-1.5 rounded-full hover:bg-[hsl(var(--elevated))]" data-testid="chat-back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="relative">
          <Avatar user={peer} size={36} />
          {isOnline(peer?.last_seen_at) && (
            <span
              className="absolute bottom-0 end-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white"
              data-testid="chat-online-dot"
              aria-label="online"
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate" data-testid="chat-peer-alias">{peer?.alias}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] truncate" data-testid="chat-presence">
            {peer?.peer_typing ? (
              <span className="text-[hsl(var(--primary))] font-medium inline-flex items-center gap-1.5" data-testid="chat-typing">
                <TypingDots /> {t("typing")}
              </span>
            ) : (
              presenceLabel(peer?.last_seen_at, t) || peer?.city || ""
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-2 bg-[hsl(var(--background))]" data-testid="chat-scroll">
        {messages.length === 0 ? (
          <div className="text-center text-sm text-[hsl(var(--muted-foreground))] py-12">
            {t("typeMessage")}
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.from_id === user.id;
            return (
              <div
                key={m.id}
                data-testid={`msg-${m.id}`}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                    mine
                      ? "bg-[hsl(var(--primary))] text-white rounded-br-md"
                      : "bg-white border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-bl-md"
                  } ${m._optimistic ? "opacity-70" : ""}`}
                >
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  <div className={`mt-1 text-[10px] ${mine ? "text-white/70" : "text-[hsl(var(--muted-foreground))]"}`}>
                    {timeShort(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      <form onSubmit={send} className="px-4 sm:px-6 py-3 border-t border-[hsl(var(--border))] bg-white flex items-center gap-2" data-testid="chat-composer">
        <input
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            const now = Date.now();
            if (now - lastTypingPing.current > 2500 && e.target.value.trim()) {
              lastTypingPing.current = now;
              api.post(`/messages/${peerId}/typing`).catch(() => {});
            }
          }}
          placeholder={t("typeMessage")}
          data-testid="chat-input"
          maxLength={2000}
          className="flex-1 px-4 py-2.5 rounded-full bg-[hsl(var(--elevated))] border border-[hsl(var(--border))] text-sm focus:outline-none focus:border-[hsl(var(--primary))]"
        />
        <button
          type="submit"
          disabled={!text.trim() || sending}
          data-testid="chat-send"
          className="h-10 w-10 inline-flex items-center justify-center rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-white disabled:opacity-50"
          aria-label={t("send")}
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
      {error && <div className="px-4 py-1.5 text-xs text-[hsl(var(--destructive))] text-center">{error}</div>}
    </div>
  );
}


function TypingDots() {
  return (
    <span className="inline-flex items-end gap-0.5 h-3" aria-hidden>
      <span className="block w-1 h-1 rounded-full bg-current animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="block w-1 h-1 rounded-full bg-current animate-bounce" style={{ animationDelay: "120ms" }} />
      <span className="block w-1 h-1 rounded-full bg-current animate-bounce" style={{ animationDelay: "240ms" }} />
    </span>
  );
}
